import apiService from './apiService';

export const expenseService = {
    // ── Books ─────────────────────────────────────────────────────────
    // `displayCurrency` is the unit to express a book's totals in when the book
    // has no `base_currency` of its own (#250). Each returned book carries the
    // `currency` its figures are actually in.
    async listBooks({ displayCurrency = null } = {}) {
        const qs = displayCurrency ? `?display_currency=${encodeURIComponent(displayCurrency)}` : '';
        return apiService.get(`/api/expenses/books${qs}`);
    },
    async createBook({ name, color }) {
        return apiService.post('/api/expenses/books', { name, color });
    },
    async updateBook(bookId, updates) {
        return apiService.patch(`/api/expenses/books/${bookId}`, updates);
    },
    // deleteTransactions=true → "Eliminate Book": also permanently delete every
    // transaction in the book (default keeps them, moving them to Unassigned).
    // #498 follow-up 2: deleteFiles=true (with deleteTransactions) also deletes the
    // book's linked File Manager files — never one another book still uses.
    async deleteBook(bookId, { deleteTransactions = false, deleteFiles = false } = {}) {
        const params = new URLSearchParams();
        if (deleteTransactions) params.set('delete_transactions', 'true');
        if (deleteTransactions && deleteFiles) params.set('delete_files', 'true');
        const qs = params.toString();
        return apiService.delete(`/api/expenses/books/${bookId}${qs ? `?${qs}` : ''}`);
    },
    // How many linked files an Eliminate could also delete → { count } (#498 follow-up 2).
    async bookLinkedFiles(bookId) {
        return apiService.get(`/api/expenses/books/${bookId}/linked-files`);
    },

    // ── Expenses ──────────────────────────────────────────────────────
    async listExpenses({ bookId = null, startDate = null, endDate = null } = {}) {
        const params = new URLSearchParams();
        if (bookId) params.set('book_id', bookId);
        if (startDate) params.set('start_date', startDate);
        if (endDate) params.set('end_date', endDate);
        const qs = params.toString();
        return apiService.get(`/api/expenses${qs ? `?${qs}` : ''}`);
    },
    async summary({ date = null, startDate = null, endDate = null, displayCurrency = null } = {}) {
        const params = new URLSearchParams();
        if (date) params.set('date', date);
        if (startDate) params.set('start_date', startDate);
        if (endDate) params.set('end_date', endDate);
        if (displayCurrency) params.set('display_currency', displayCurrency);
        const qs = params.toString();
        return apiService.get(`/api/expenses/summary${qs ? `?${qs}` : ''}`);
    },
    // Finance Visualization Mode (#442): per-period + per-category aggregates for
    // ONE book ('unassigned' or a book id), converted SERVER-SIDE into the book's
    // effective currency — never summed client-side across currencies.
    async analytics({ bookId, startDate, endDate, granularity = 'day', displayCurrency = null } = {}) {
        const params = new URLSearchParams();
        params.set('book_id', bookId || 'unassigned');
        if (startDate) params.set('start_date', startDate);
        if (endDate) params.set('end_date', endDate);
        if (granularity) params.set('granularity', granularity);
        if (displayCurrency) params.set('display_currency', displayCurrency);
        return apiService.get(`/api/expenses/analytics?${params.toString()}`);
    },
    // Interactive category (#483): the transactions behind ONE pie slice — same
    // scope params as analytics() plus the pie's direction and the slice's
    // category label(s) ("Other" sends every folded label).
    async analyticsCategory({ bookId, startDate, endDate, displayCurrency = null, direction, categories = [] } = {}) {
        const params = new URLSearchParams();
        params.set('book_id', bookId || 'unassigned');
        if (startDate) params.set('start_date', startDate);
        if (endDate) params.set('end_date', endDate);
        if (displayCurrency) params.set('display_currency', displayCurrency);
        params.set('direction', direction === 'income' ? 'income' : 'expense');
        (categories || []).forEach((c) => params.append('category', c));
        return apiService.get(`/api/expenses/analytics/category?${params.toString()}`);
    },
    async listCurrencies() {
        return apiService.get('/api/expenses/currencies');
    },
    async createExpense(data) {
        return apiService.post('/api/expenses', data);
    },
    async updateExpense(expenseId, updates) {
        return apiService.patch(`/api/expenses/${expenseId}`, updates);
    },
    // Transparent FX breakdown for one expense at its transaction-date rate
    // (Exchange rate #67) — original/converted amounts + the historical rate used.
    async getConversion(expenseId, toCurrency) {
        const qs = toCurrency ? `?to=${encodeURIComponent(toCurrency)}` : '';
        return apiService.get(`/api/expenses/${expenseId}/conversion${qs}`);
    },
    // Recurring 2.0 addendum (#234 re-open): the eliminate/cleanup IMPACT for a
    // recurring template — the whole-lineage occurrence_count + the preserved
    // source (merchant + original date). Drives the stop-cleanup & delete dialogs.
    async recurrenceImpact(expenseId) {
        return apiService.get(`/api/expenses/${expenseId}/recurrence-impact`);
    },
    // Recurring 2.0 (#234): for a recurring TEMPLATE, occurrences='keep' detaches
    // its generated occurrences so they survive as independent rows (the FK is ON
    // DELETE CASCADE); 'eliminate'/absent removes them with the template.
    // #498 follow-up: the linked File Manager document is KEPT unless the user
    // explicitly ticked "delete the attached file as well" (deleteFiles=true).
    async deleteExpense(expenseId, { occurrences = null, deleteFiles = false } = {}) {
        const params = new URLSearchParams();
        if (occurrences) params.set('occurrences', occurrences);
        if (deleteFiles) params.set('delete_files', 'true');
        const qs = params.toString();
        return apiService.delete(`/api/expenses/${expenseId}${qs ? `?${qs}` : ''}`);
    },
    /**
     * Bulk move/delete. operation = 'delete' | 'move'; for 'move', pass either
     * a bookId UUID or the literal string 'unassigned'. For 'delete', linked
     * files are kept unless deleteFiles=true (#498 follow-up).
     */
    async bulk({ ids, operation, bookId = null, deleteFiles = false }) {
        return apiService.post('/api/expenses/bulk', {
            ids,
            operation,
            book_id: bookId,
            ...(operation === 'delete' ? { delete_files: !!deleteFiles } : {}),
        });
    },

    // ── Members (Shared Books) ────────────────────────────────────────
    async listMembers(bookId) {
        return apiService.get(`/api/expenses/books/${bookId}/members`);
    },
    // Invite by invitee_user_id (Members UI management #194 — the Shared Space
    // picker resolves a network contact to a real user id) or by email
    // (back-compat). Undefined keys are dropped by JSON.stringify.
    async addMember(bookId, { email, invitee_user_id, role = 'member' } = {}) {
        return apiService.post(`/api/expenses/books/${bookId}/members`, { email, invitee_user_id, role });
    },
    async updateMember(bookId, userId, { role }) {
        return apiService.patch(`/api/expenses/books/${bookId}/members/${userId}`, { role });
    },
    async removeMember(bookId, userId) {
        return apiService.delete(`/api/expenses/books/${bookId}/members/${userId}`);
    },
    // #194 Team→Book shares: remove a whole TEAM from this book (the exact twin
    // of boardService.removeTeamShare — platform parity).
    // → { unshared, kind:'book', members_removed }
    async removeTeamShare(bookId, teamId) {
        return apiService.delete(`/api/expenses/books/${bookId}/team-shares/${teamId}`);
    },
};

export default expenseService;
